import React from 'react';

interface ListLoaderProps {
  /** Number of skeleton items to display */
  count?: number;
  /** Custom className for the container */
  className?: string;
  /** Show divider between items */
  showDivider?: boolean;
}

interface ShimmerProps {
  className?: string;
  style?: React.CSSProperties;
}

const Shimmer: React.FC<ShimmerProps> = ({ className = '', style }) => {
  const shimmerStyle: React.CSSProperties = {
    backgroundImage: `
      linear-gradient(0deg, #F1F0F4, #F1F0F4),
      linear-gradient(270deg, rgba(248, 247, 251, 0) 0%, #F1F0F4 50%, rgba(248, 247, 251, 0) 100%)
    `,
    backgroundSize: '100% 100%, 200% 100%',
    backgroundPosition: '0 0, -200% 0',
    animation: 'shimmer 1.4s ease-in-out infinite',
    ...style,
  };

  return (
    <>
      <style>
        {`
          @keyframes shimmer {
            0% {
              background-position: 0 0, -200% 0;
            }
            100% {
              background-position: 0 0, 200% 0;
            }
          }
        `}
      </style>
      <div className={`rounded-full ${className}`} style={shimmerStyle} />
    </>
  );
};

const ListLoader: React.FC<ListLoaderProps> = ({
  count = 6,
  className = '',
  showDivider = true,
}) => {
  return (
    <div className="p-6 bg-white rounded-2xl">
      <div className={`p-4 ${className} animate-pulse`}>
        {Array.from({ length: count }).map((_, index) => (
          <div key={index} className={showDivider ? 'border-b border-neutral-95' : ''}>
            <div className="w-full">
              <div className="flex items-center justify-between py-4">
                {/* Left side */}
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  <Shimmer className="w-12 h-12 rounded-full" />

                  {/* Lines */}
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Shimmer className="w-8 h-4 md:w-10" />
                      <Shimmer className="w-24 h-4 md:w-52" />
                    </div>
                    <Shimmer className="w-32 h-4 md:w-64" />
                  </div>
                </div>

                {/* Right pill */}
                <Shimmer className="w-16 h-4 rounded-full md:w-24" />
              </div>

              {/* Divider */}
              {showDivider && index < count - 1 && (
                <div className="h-px w-full bg-[#F1F0F4]" />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>

  );
};

export { ListLoader };
